import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Inbox, Laptop, Plus, School, TriangleAlert, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { IntegrationStatusBadge } from '../components/badges';
import { CopyField } from '../components/integration-shell';
import { PlanCell } from '../components/platform-billing';
import { PlanModal } from '../components/platform-plan';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Select,
  StatCard,
  Switch,
  Table,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatNumber } from '../lib/format';
import { PROVIDER_LABEL } from '../lib/integrations';
import { openSchool } from '../lib/school';
import type { PlatformTenant, TenantStatus } from '../lib/types';

interface Overview {
  tenants: Partial<Record<TenantStatus, number>>;
  users: number;
  assets: number;
  people: number;
  devices: number;
  failedSyncs24h: number;
  newIntegrationRequests: number;
  /** Schools with a Stripe subscription that's paid up (or in its grace period), and those past due. */
  billing: { paying: number; pastDue: number; enforced: boolean };
}

interface CreatedSchool {
  tenant: PlatformTenant;
  admin: { email: string; temporaryPassword?: string; signsInWithGoogle: boolean };
  signInUrl: string;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

const EMPTY_FORM = {
  name: '',
  slug: '',
  timezone: 'America/New_York',
  contactEmail: '',
  status: 'ACTIVE' as TenantStatus,
  adminName: '',
  adminEmail: '',
  googleDomain: '',
  demoData: false,
  trialDays: '30',
};

function CreateSchoolModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [created, setCreated] = useState<CreatedSchool | null>(null);
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedSchool>('/platform/tenants', {
        name: form.name,
        slug: form.slug,
        timezone: form.timezone,
        contactEmail: form.contactEmail,
        status: form.status,
        admin: { name: form.adminName, email: form.adminEmail },
        googleDomain: form.googleDomain,
        demoData: form.demoData,
        trialDays: form.trialDays === '' ? 0 : Number(form.trialDays),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      setCreated(result);
      toast.success(`Created ${result.tenant.name}`);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const close = () => {
    setCreated(null);
    setError('');
    setSlugTouched(false);
    setForm(EMPTY_FORM);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={created ? 'School created' : 'New school'}
      footer={
        created ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Create school
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            <strong>{created.tenant.name}</strong> is ready. {created.admin.email} is its administrator.
          </p>
          {created.admin.signsInWithGoogle ? (
            <p>They sign in with their school Google account on the school’s sign-in page. No password was created.</p>
          ) : created.admin.temporaryPassword ? (
            <>
              <p>Share this temporary password securely - it won’t be shown again:</p>
              <Input readOnly value={created.admin.temporaryPassword} className="font-mono" onFocus={(e) => e.target.select()} />
            </>
          ) : (
            <p>They already had an Edvora account and can sign in the way they do today.</p>
          )}
          <CopyField label="Sign-in page" value={created.signInUrl} />
        </div>
      ) : (
        <div className="space-y-5">
          {error && <Alert tone="red">{error}</Alert>}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="School name" required>
              <Input
                value={form.name}
                autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })}
              />
            </Field>
            <Field label="Address" hint={`${window.location.host}/${form.slug || 'school'}`}>
              <Input
                value={form.slug}
                className="font-mono"
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm({ ...form, slug: e.target.value });
                }}
              />
            </Field>
            <Field label="Time zone">
              <Select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
                {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'UTC'].map((tz) => (
                  <option key={tz}>{tz}</option>
                ))}
              </Select>
            </Field>
            <Field label="Account status">
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as TenantStatus })}>
                <option value="ACTIVE">Active</option>
                <option value="TRIAL">Trial</option>
              </Select>
            </Field>
            <Field label="Technology contact email">
              <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </Field>
            <Field label="Free trial (days)" hint="Everything is included until it ends, then the school subscribes. 0 for none.">
              <Input
                type="number"
                min={0}
                max={365}
                value={form.trialDays}
                onChange={(e) => setForm({ ...form, trialDays: e.target.value })}
                className="w-28"
              />
            </Field>
          </div>
          <div>
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">Sign-in</h3>
            <Field
              label="Google Workspace domain"
              hint="Staff and students sign in with Google accounts on this domain, and no passwords are issued. Leave empty to start with passwords."
            >
              <Input value={form.googleDomain} onChange={(e) => setForm({ ...form, googleDomain: e.target.value })} placeholder="school.org" className="font-mono" />
            </Field>
          </div>
          <div>
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">First administrator</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" required>
                <Input value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} />
              </Field>
              <Field label="Email" required hint={form.googleDomain ? `Must be on ${form.googleDomain}` : undefined}>
                <Input type="email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} />
              </Field>
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-4 py-3">
            <input type="checkbox" className="mt-1" checked={form.demoData} onChange={(e) => setForm({ ...form, demoData: e.target.checked })} />
            <span>
              <span className="block text-sm font-medium text-slate-900">Start with demo data</span>
              <span className="block text-xs text-slate-500">
                Turns on Blackbaud, Google, Jamf Pro, and Apple School Manager in demo mode and runs a first sync - good for trials.
              </span>
            </span>
          </label>
        </div>
      )}
    </Modal>
  );
}

export function PlatformPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const overview = useQuery({ queryKey: ['platform', 'overview'], queryFn: () => api.get<Overview>('/platform/overview') });
  const tenants = useQuery({ queryKey: ['platform', 'tenants'], queryFn: () => api.get<PlatformTenant[]>('/platform/tenants') });

  const [facilitiesFor, setFacilitiesFor] = useState<PlatformTenant | null>(null);
  const [planFor, setPlanFor] = useState<PlatformTenant | null>(null);
  const setFacilities = useMutation({
    mutationFn: (tenant: PlatformTenant) => api.patch(`/platform/tenants/${tenant.id}`, { facilitiesEnabled: !tenant.facilitiesEnabled }),
    onSuccess: (_result, tenant) => {
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      setFacilitiesFor(null);
      toast.success(tenant.facilitiesEnabled ? `Facilities is off for ${tenant.name}` : `Facilities is on for ${tenant.name}`);
    },
    onError: (err) => {
      setFacilitiesFor(null);
      toast.error(errorMessage(err));
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) => api.patch(`/platform/tenants/${id}`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      toast.success('School updated');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (tenants.isLoading) return <PageLoader />;
  if (tenants.error) return <ErrorState error={tenants.error} onRetry={() => tenants.refetch()} />;

  const totalSchools = Object.values(overview.data?.tenants ?? {}).reduce((sum, n) => sum + (n ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Edvora platform"
        description="Every school environment on this deployment."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
            New school
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Schools"
          value={formatNumber(totalSchools)}
          sub={`${formatNumber(overview.data?.billing.paying)} paying · ${formatNumber(overview.data?.tenants.TRIAL)} trial · ${formatNumber(overview.data?.tenants.SUSPENDED)} suspended`}
          icon={<School />}
        />
        <StatCard label="Users" value={formatNumber(overview.data?.users)} icon={<Users />} tone="violet" />
        <StatCard label="Assets managed" value={formatNumber(overview.data?.assets)} sub={`${formatNumber(overview.data?.devices)} synced devices`} icon={<Boxes />} tone="green" />
        <StatCard
          label="Failed syncs (24h)"
          value={formatNumber(overview.data?.failedSyncs24h)}
          icon={overview.data?.failedSyncs24h ? <TriangleAlert /> : <Laptop />}
          tone={overview.data?.failedSyncs24h ? 'red' : 'green'}
        />
      </div>

      {!!overview.data?.newIntegrationRequests && (
        <Alert tone="blue" icon={<Inbox />} className="mt-6" title={`${formatNumber(overview.data.newIntegrationRequests)} new integration request${overview.data.newIntegrationRequests === 1 ? '' : 's'}`}>
          Schools asked for integrations Edvora doesn’t have yet.{' '}
          <Link to="/platform/integration-requests" className="font-medium underline">
            Review requests
          </Link>
        </Alert>
      )}

      {overview.data?.billing.enforced === false && (
        <Alert tone="blue" className="mt-6" title="Billing isn’t enforced on this environment">
          A school with no trial and no complimentary plan has every module, whatever Stripe says. What a trial or a
          complimentary plan includes still holds: tick only the modules a school should have.
        </Alert>
      )}

      {!!overview.data?.billing.pastDue && (
        <Alert tone="amber" className="mt-6" title={`${formatNumber(overview.data.billing.pastDue)} school${overview.data.billing.pastDue === 1 ? '' : 's'} past due`}>
          Stripe couldn’t collect their last payment and is retrying. They keep Edvora until Stripe gives up.
        </Alert>
      )}

      <Card className="mt-6">
        <CardHeader title="Schools" />
        <Table>
          <thead>
            <tr>
              <Th>School</Th>
              <Th>Status</Th>
              <Th>Plan</Th>
              <Th>Users</Th>
              <Th>Assets</Th>
              <Th>People</Th>
              <Th>Devices</Th>
              <Th>Integrations</Th>
              <Th>Facilities</Th>
              <Th>Created</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tenants.data!.map((tenant) => (
              <tr key={tenant.id}>
                <Td>
                  <p className="font-medium text-slate-900">{tenant.name}</p>
                  <p className="font-mono text-xs text-slate-500">
                    /{tenant.slug}
                    {tenant.googleSignIn && <Badge className="ml-2">Google sign-in</Badge>}
                  </p>
                </Td>
                <Td>
                  <Badge tone={tenant.status === 'ACTIVE' ? 'green' : tenant.status === 'TRIAL' ? 'amber' : 'red'}>
                    {tenant.status.charAt(0) + tenant.status.slice(1).toLowerCase()}
                  </Badge>
                </Td>
                <Td>
                  <PlanCell billing={tenant.billing} />
                </Td>
                <Td>{tenant._count.memberships}</Td>
                <Td>{formatNumber(tenant._count.assets)}</Td>
                <Td>{formatNumber(tenant._count.people)}</Td>
                <Td>{formatNumber(tenant._count.googleDevices + tenant._count.managedDevices)}</Td>
                <Td>
                  <div className="flex flex-col gap-1">
                    {tenant.integrations
                      .filter((integration) => integration.enabled || integration.status !== 'NOT_CONFIGURED')
                      .map((integration) => (
                        <span key={integration.provider} className="flex items-center gap-1.5 text-xs text-slate-600">
                          {PROVIDER_LABEL[integration.provider]}
                          <IntegrationStatusBadge status={integration.status} enabled={integration.enabled} />
                          {integration.mode === 'DEMO' && integration.enabled && <span className="text-slate-400">demo</span>}
                        </span>
                      ))}
                  </div>
                </Td>
                <Td>
                  <Switch
                    checked={tenant.facilitiesEnabled}
                    onChange={() => setFacilitiesFor(tenant)}
                    disabled={setFacilities.isPending}
                    label={tenant.facilitiesEnabled ? 'On' : 'Off'}
                  />
                </Td>
                <Td className="whitespace-nowrap text-slate-500">{formatDate(tenant.createdAt)}</Td>
                <Td className="text-right whitespace-nowrap">
                  <Button size="sm" onClick={() => openSchool(tenant.slug)}>
                    Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPlanFor(tenant)}>
                    Plan
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setStatus.mutate({ id: tenant.id, status: tenant.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED' })}
                  >
                    {tenant.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <ConfirmDialog
        open={!!facilitiesFor}
        onClose={() => setFacilitiesFor(null)}
        onConfirm={() => facilitiesFor && setFacilities.mutate(facilitiesFor)}
        loading={setFacilities.isPending}
        tone={facilitiesFor?.facilitiesEnabled ? 'danger' : 'primary'}
        title={facilitiesFor?.facilitiesEnabled ? `Turn off Facilities for ${facilitiesFor.name}?` : `Turn on Facilities for ${facilitiesFor?.name}?`}
        description={
          facilitiesFor?.facilitiesEnabled
            ? 'The school’s Facilities pages, requests, and facilities mailbox are hidden. Nothing is deleted, and turning it back on brings everything back.'
            : 'The school gets a Facilities section: maintenance requests and work orders, preventive maintenance, inspections, equipment, documents, and reports. The first time, it starts with sample categories, a routing rule, and inspection templates. School administrators give people facilities roles under Users & roles.'
        }
        confirmLabel={facilitiesFor?.facilitiesEnabled ? 'Turn off Facilities' : 'Turn on Facilities'}
      />
      <CreateSchoolModal open={creating} onClose={() => setCreating(false)} />
      {planFor && <PlanModal key={planFor.id} tenant={planFor} onClose={() => setPlanFor(null)} />}
    </>
  );
}
